/**
 * FinTasks Service
 * Manages todo lists and tasks with financial awareness
 */

import { db } from '@/config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  writeBatch,
  onSnapshot
} from 'firebase/firestore';

// Debug logging - only in dev with VITE_DEBUG_TASKS=true
const DEBUG_TASKS = import.meta.env.DEV && import.meta.env.VITE_DEBUG_TASKS === 'true';

// ============================================
// CONSTANTS & TYPES
// ============================================

export const TASK_STATUS = {
  TODO: 'todo',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
  ARCHIVED: 'archived'
};

export const TASK_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
};

export const LIST_CONTEXT = {
  PERSONAL: 'personal',
  HOUSEHOLD: 'household'
};

export const RECURRENCE_TYPE = {
  NONE: 'none',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  CUSTOM: 'custom'
};

export const LINKED_TYPE = {
  SHOPPING_ITEM: 'shoppingItem',
  SHOPPING_LIST: 'shoppingList',
  BUDGET: 'budget',
  TRANSACTION: 'transaction',
  BILL: 'bill',
  NONE: 'none'
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const parseTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  return null;
};

const normalizeTaskFromFirestore = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    createdAt: parseTimestamp(data.createdAt),
    updatedAt: parseTimestamp(data.updatedAt),
    completedAt: parseTimestamp(data.completedAt),
    dueDate: parseTimestamp(data.dueDate),
    reminderAt: parseTimestamp(data.reminderAt),
    // FINLIST ENVELOPE SYSTEM: Include envelope and goal links
    linkedEnvelopeId: data.linkedEnvelopeId || null,
    goalId: data.goalId || null
  };
};

const normalizeListFromFirestore = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    createdAt: parseTimestamp(data.createdAt),
    updatedAt: parseTimestamp(data.updatedAt)
  };
};

// ============================================
// TODO LISTS CRUD
// ============================================

/**
 * Get all todo lists for a user
 */
export async function getTodoLists(userId) {
  if (!userId) throw new Error('User ID is required');

  try {
    const listsRef = collection(db, 'users', userId, 'todoLists');
    const snapshot = await getDocs(listsRef);
    
    if (DEBUG_TASKS) console.log('getTodoLists - Raw snapshot:', {
      userId,
      docCount: snapshot.docs.length,
      docs: snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
    });

    // Filter and sort in memory to avoid Firestore index requirement
    const allLists = snapshot.docs
      .map(normalizeListFromFirestore)
      .filter(list => list.archived !== true)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    if (DEBUG_TASKS) console.log('getTodoLists - Filtered lists:', allLists);
    return allLists;
  } catch (error) {
    console.error('Error fetching todo lists:', error);
    throw error;
  }
}

/**
 * Get a single todo list by ID
 */
export async function getTodoListById(userId, listId) {
  if (!userId || !listId) throw new Error('User ID and List ID are required');

  try {
    const listRef = doc(db, 'users', userId, 'todoLists', listId);
    const snapshot = await getDoc(listRef);

    if (!snapshot.exists()) {
      throw new Error('Todo list not found');
    }

    return normalizeListFromFirestore(snapshot);
  } catch (error) {
    console.error('Error fetching todo list:', error);
    throw error;
  }
}

/**
 * Create a new todo list
 */
export async function createTodoList(userId, listData) {
  if (!userId) throw new Error('User ID is required');

  console.log('createTodoList called with:', { userId, listData });

  const newList = {
    ownerId: userId,
    title: listData.title || 'Untitled List',
    description: listData.description || '',
    context: listData.context || LIST_CONTEXT.PERSONAL,
    icon: listData.icon || '📝',
    color: listData.color || '#06D6A0',
    isDefault: listData.isDefault || false,
    sortOrder: listData.sortOrder || 0,
    archived: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  try {
    const listsRef = collection(db, 'users', userId, 'todoLists');
    const newDocRef = doc(listsRef);
    
    console.log('Creating todo list at path:', `users/${userId}/todoLists/${newDocRef.id}`);
    await setDoc(newDocRef, newList);

    const createdList = {
      id: newDocRef.id,
      ...newList,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log('Todo list created successfully:', createdList);
    return createdList;
  } catch (error) {
    console.error('Error creating todo list:', error);
    throw error;
  }
}

/**
 * Update an existing todo list
 */
export async function updateTodoList(userId, listId, updates) {
  if (!userId || !listId) throw new Error('User ID and List ID are required');

  try {
    const listRef = doc(db, 'users', userId, 'todoLists', listId);
    
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // Remove undefined fields
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) delete updateData[key];
    });

    await updateDoc(listRef, updateData);
    return true;
  } catch (error) {
    console.error('Error updating todo list:', error);
    throw error;
  }
}

/**
 * Delete a todo list (soft delete by archiving)
 */
export async function archiveTodoList(userId, listId) {
  if (!userId || !listId) throw new Error('User ID and List ID are required');

  try {
    await updateTodoList(userId, listId, { archived: true });
    return true;
  } catch (error) {
    console.error('Error archiving todo list:', error);
    throw error;
  }
}

/**
 * Permanently delete a todo list and all its tasks
 */
export async function deleteTodoList(userId, listId) {
  if (!userId || !listId) throw new Error('User ID and List ID are required');

  try {
    const batch = writeBatch(db);

    // Delete all tasks in the list
    const tasksRef = collection(db, 'users', userId, 'todoLists', listId, 'tasks');
    const tasksSnapshot = await getDocs(tasksRef);
    tasksSnapshot.docs.forEach((taskDoc) => {
      batch.delete(taskDoc.ref);
    });

    // Delete the list itself
    const listRef = doc(db, 'users', userId, 'todoLists', listId);
    batch.delete(listRef);

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error deleting todo list:', error);
    throw error;
  }
}

// ============================================
// TASKS CRUD
// ============================================

/**
 * Get all tasks for a specific list
 */
export async function getTasksByList(userId, listId, options = {}) {
  if (!userId || !listId) throw new Error('User ID and List ID are required');

  try {
    const tasksRef = collection(db, 'users', userId, 'todoLists', listId, 'tasks');
    let q = query(tasksRef);

    // Apply filters
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }

    if (options.priority) {
      q = query(q, where('priority', '==', options.priority));
    }

    if (options.isFinancialTask !== undefined) {
      q = query(q, where('isFinancialTask', '==', options.isFinancialTask));
    }

    // Apply ordering
    const orderField = options.orderBy || 'createdAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderField, orderDirection));

    // Apply limit
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(normalizeTaskFromFirestore);
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
}

/**
 * Get all tasks for a user across all lists
 */
export async function getAllTasks(userId, options = {}) {
  if (!userId) throw new Error('User ID is required');

  try {
    // First get all lists
    const lists = await getTodoLists(userId);
    
    // Then fetch tasks from each list
    const allTasks = [];
    for (const list of lists) {
      const tasks = await getTasksByList(userId, list.id, options);
      allTasks.push(...tasks.map(task => ({ ...task, listId: list.id, listTitle: list.title })));
    }

    return allTasks;
  } catch (error) {
    console.error('Error fetching all tasks:', error);
    throw error;
  }
}

/**
 * Get tasks filtered by various criteria
 */
export async function getFilteredTasks(userId, filter = 'all') {
  if (!userId) throw new Error('User ID is required');

  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const allTasks = await getAllTasks(userId);
    
    console.log('[getFilteredTasks] Filter:', filter, 'Total tasks:', allTasks.length);
    console.log('[getFilteredTasks] Tasks:', allTasks.map(t => ({ 
      id: t.id, 
      title: t.title, 
      status: t.status, 
      dueDate: t.dueDate 
    })));

    switch (filter) {
      case 'today':
        return allTasks.filter(task => {
          if (!task.dueDate || task.status === TASK_STATUS.DONE || task.status === TASK_STATUS.ARCHIVED) return false;
          const dueDate = new Date(task.dueDate);
          return dueDate >= today && dueDate < tomorrow;
        });

      case 'upcoming':
        return allTasks.filter(task => {
          if (!task.dueDate || task.status === TASK_STATUS.DONE || task.status === TASK_STATUS.ARCHIVED) return false;
          const dueDate = new Date(task.dueDate);
          return dueDate >= tomorrow;
        });

      case 'overdue':
        return allTasks.filter(task => {
          // Exclude done and archived tasks
          if (!task.dueDate || task.status === TASK_STATUS.DONE || task.status === TASK_STATUS.ARCHIVED) return false;
          const dueDate = new Date(task.dueDate);
          // A task is overdue if its due date/time is before NOW (not just before today)
          return dueDate < now;
        });

      case 'completed':
        return allTasks.filter(task => task.status === TASK_STATUS.DONE);

      case 'archived':
        return allTasks.filter(task => task.status === TASK_STATUS.ARCHIVED);

      case 'financial':
        return allTasks.filter(task => task.isFinancialTask === true && task.status !== TASK_STATUS.ARCHIVED);

      case 'savings':
        // Filter tasks linked to savings goals
        return allTasks.filter(task => task.goalId && task.status !== TASK_STATUS.ARCHIVED);

      case 'all':
      default: {
        // Show all non-archived tasks
        const result = allTasks.filter(task => task.status !== TASK_STATUS.ARCHIVED);
        console.log('[getFilteredTasks] Returning', result.length, 'non-archived tasks');
        return result;
      }
    }
  } catch (error) {
    console.error('Error fetching filtered tasks:', error);
    throw error;
  }
}

/**
 * Get a single task by ID
 */
export async function getTaskById(userId, listId, taskId) {
  if (!userId || !listId || !taskId) {
    throw new Error('User ID, List ID, and Task ID are required');
  }

  try {
    const taskRef = doc(db, 'users', userId, 'todoLists', listId, 'tasks', taskId);
    const snapshot = await getDoc(taskRef);

    if (!snapshot.exists()) {
      throw new Error('Task not found');
    }

    return normalizeTaskFromFirestore(snapshot);
  } catch (error) {
    console.error('Error fetching task:', error);
    throw error;
  }
}

/**
 * Create a new task
 * FINLIST ENVELOPE SYSTEM: Tasks can optionally link to Budget envelopes and Goals
 */
export async function createTask(userId, listId, taskData) {
  if (!userId || !listId) throw new Error('User ID and List ID are required');

  const newTask = {
    listId,
    ownerId: userId,
    title: taskData.title || 'Untitled Task',
    description: taskData.description || '',
    status: taskData.status || TASK_STATUS.TODO,
    priority: taskData.priority || TASK_PRIORITY.MEDIUM,
    dueDate: taskData.dueDate ? Timestamp.fromDate(new Date(taskData.dueDate)) : null,
    reminderAt: taskData.reminderAt ? Timestamp.fromDate(new Date(taskData.reminderAt)) : null,
    isFinancialTask: taskData.isFinancialTask || false,
    estimatedAmount: taskData.estimatedAmount || null,
    currency: taskData.currency || 'GBP',
    linkedType: taskData.linkedType || LINKED_TYPE.NONE,
    linkedRef: taskData.linkedRef || null,
    linkedLabel: taskData.linkedLabel || null,
    recurrence: taskData.recurrence || { type: RECURRENCE_TYPE.NONE },
    tags: taskData.tags || [],
    // FINLIST ENVELOPE SYSTEM: Optional link to Budget envelope (Block C)
    // Tasks can reference an envelope for smart suggestions and expense conversion
    linkedEnvelopeId: taskData.linkedEnvelopeId || null,
    // FINLIST ENVELOPE SYSTEM: Optional link to Goal for goal-related tasks
    goalId: taskData.goalId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    completedAt: null
  };

  try {
    const tasksRef = collection(db, 'users', userId, 'todoLists', listId, 'tasks');
    const newDocRef = doc(tasksRef);
    await setDoc(newDocRef, newTask);

    // Update todoMeta
    await updateTodoMeta(userId);

    return {
      id: newDocRef.id,
      ...newTask,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
}

/**
 * Update an existing task
 */
export async function updateTask(userId, listId, taskId, updates) {
  if (!userId || !listId || !taskId) {
    throw new Error('User ID, List ID, and Task ID are required');
  }

  try {
    const taskRef = doc(db, 'users', userId, 'todoLists', listId, 'tasks', taskId);
    
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // Handle date fields
    if (updates.dueDate) {
      updateData.dueDate = Timestamp.fromDate(new Date(updates.dueDate));
    }
    if (updates.reminderAt) {
      updateData.reminderAt = Timestamp.fromDate(new Date(updates.reminderAt));
    }

    // If marking as done, set completedAt
    if (updates.status === TASK_STATUS.DONE && !updates.completedAt) {
      updateData.completedAt = serverTimestamp();
    }

    // If unmarking as done, clear completedAt
    if (updates.status !== TASK_STATUS.DONE && updates.status !== undefined) {
      updateData.completedAt = null;
    }

    // Remove undefined fields
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) delete updateData[key];
    });

    await updateDoc(taskRef, updateData);

    // Update todoMeta
    await updateTodoMeta(userId);

    return true;
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
}

/**
 * Move a task between lists (preserves id and createdAt)
 */
export async function moveTask(userId, fromListId, toListId, taskId, updates = {}) {
  if (!userId || !fromListId || !toListId || !taskId) {
    throw new Error('User ID, from/to List IDs, and Task ID are required');
  }

  try {
    // Read existing task
    const fromRef = doc(db, 'users', userId, 'todoLists', fromListId, 'tasks', taskId);
    const snapshot = await getDoc(fromRef);
    if (!snapshot.exists()) {
      throw new Error('Source task not found');
    }

    const existing = snapshot.data();

    // Prepare merged data
    const merged = {
      ...existing,
      ...updates,
      listId: toListId,
      updatedAt: serverTimestamp()
    };

    // Handle date fields in updates
    if (updates.dueDate) {
      merged.dueDate = Timestamp.fromDate(new Date(updates.dueDate));
    }
    if (updates.reminderAt) {
      merged.reminderAt = Timestamp.fromDate(new Date(updates.reminderAt));
    }

    // Write to destination (preserve id)
    const toRef = doc(db, 'users', userId, 'todoLists', toListId, 'tasks', taskId);
    await setDoc(toRef, merged, { merge: true });

    // Delete from source
    await deleteDoc(fromRef);

    // Update meta
    await updateTodoMeta(userId);

    return true;
  } catch (error) {
    console.error('Error moving task:', error);
    throw error;
  }
}

/**
 * Toggle task status between todo and done
 */
export async function toggleTaskStatus(userId, listId, taskId) {
  if (!userId || !listId || !taskId) {
    throw new Error('User ID, List ID, and Task ID are required');
  }

  try {
    const task = await getTaskById(userId, listId, taskId);
    const newStatus = task.status === TASK_STATUS.DONE ? TASK_STATUS.TODO : TASK_STATUS.DONE;

    await updateTask(userId, listId, taskId, { status: newStatus });
    return newStatus;
  } catch (error) {
    console.error('Error toggling task status:', error);
    throw error;
  }
}

/**
 * Archive a task
 */
export async function archiveTask(userId, listId, taskId) {
  console.log('[finTasksService.archiveTask] Called with:', { userId, listId, taskId });
  
  if (!userId || !listId || !taskId) {
    console.error('[finTasksService.archiveTask] Missing required params:', { userId, listId, taskId });
    throw new Error('User ID, List ID, and Task ID are required');
  }

  try {
    await updateTask(userId, listId, taskId, { 
      status: TASK_STATUS.ARCHIVED,
      archivedAt: serverTimestamp()
    });
    console.log('[finTasksService.archiveTask] Success - task archived');
    return true;
  } catch (error) {
    console.error('Error archiving task:', error);
    throw error;
  }
}

/**
 * Unarchive a task
 */
export async function unarchiveTask(userId, listId, taskId) {
  if (!userId || !listId || !taskId) {
    throw new Error('User ID, List ID, and Task ID are required');
  }

  try {
    const task = await getTaskById(userId, listId, taskId);
    const status = task.completedAt ? TASK_STATUS.DONE : TASK_STATUS.TODO;
    
    await updateTask(userId, listId, taskId, { 
      status,
      archivedAt: null
    });
    return status;
  } catch (error) {
    console.error('Error unarchiving task:', error);
    throw error;
  }
}

/**
 * Delete a task
 */
export async function deleteTask(userId, listId, taskId) {
  if (!userId || !listId || !taskId) {
    throw new Error('User ID, List ID, and Task ID are required');
  }

  try {
    const taskRef = doc(db, 'users', userId, 'todoLists', listId, 'tasks', taskId);
    await deleteDoc(taskRef);

    // Update todoMeta
    await updateTodoMeta(userId);

    return true;
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
}

// ============================================
// TODO META (ANALYTICS)
// ============================================

/**
 * Get todo meta summary
 */
export async function getTodoMeta(userId) {
  if (!userId) throw new Error('User ID is required');

  try {
    const metaRef = doc(db, 'users', userId, 'todoMeta', 'summary');
    const snapshot = await getDoc(metaRef);

    if (!snapshot.exists()) {
      // Initialize if doesn't exist
      await updateTodoMeta(userId);
      return await getTodoMeta(userId);
    }

    const data = snapshot.data();
    return {
      ...data,
      updatedAt: parseTimestamp(data.updatedAt)
    };
  } catch (error) {
    console.error('Error fetching todo meta:', error);
    throw error;
  }
}

/**
 * Update todo meta summary (calculate stats from tasks)
 */
export async function updateTodoMeta(userId) {
  if (!userId) throw new Error('User ID is required');

  try {
    const allTasks = await getAllTasks(userId);
    
    const totalTasks = allTasks.filter(t => t.status !== TASK_STATUS.ARCHIVED).length;
    const completedTasks = allTasks.filter(t => t.status === TASK_STATUS.DONE).length;
    const financialTasksCount = allTasks.filter(t => t.isFinancialTask === true).length;
    
    // Calculate overdue count
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const overdueCount = allTasks.filter(task => {
      if (!task.dueDate || task.status === TASK_STATUS.DONE) return false;
      const dueDate = new Date(task.dueDate);
      return dueDate < today;
    }).length;

    // Calculate 7-day completion rate
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const recentTasks = allTasks.filter(task => {
      const createdAt = new Date(task.createdAt);
      return createdAt >= sevenDaysAgo;
    });
    
    const recentCompleted = recentTasks.filter(t => t.status === TASK_STATUS.DONE).length;
    const completionRate7d = recentTasks.length > 0 
      ? Math.round((recentCompleted / recentTasks.length) * 100) 
      : 0;

    // Calculate streak (consecutive days with completed tasks)
    const streakDays = calculateStreak(allTasks);

    const metaData = {
      totalTasks,
      completedTasks,
      completionRate7d,
      financialTasksCount,
      overdueCount,
      streakDays,
      updatedAt: serverTimestamp()
    };

    const metaRef = doc(db, 'users', userId, 'todoMeta', 'summary');
    await setDoc(metaRef, metaData, { merge: true });

    return metaData;
  } catch (error) {
    console.error('Error updating todo meta:', error);
    throw error;
  }
}

/**
 * Calculate completion streak in days
 */
function calculateStreak(tasks) {
  const completedTasks = tasks
    .filter(t => t.status === TASK_STATUS.DONE && t.completedAt)
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

  if (completedTasks.length === 0) return 0;

  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let currentDate = new Date(today);
  
  for (let i = 0; i < 30; i++) { // Check up to 30 days back
    const hasTaskThisDay = completedTasks.some(task => {
      const completedDate = new Date(task.completedAt);
      completedDate.setHours(0, 0, 0, 0);
      return completedDate.getTime() === currentDate.getTime();
    });

    if (hasTaskThisDay) {
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      break;
    }
  }

  return streak;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Create default task list for new users
 */
export async function createDefaultLists(userId) {
  if (!userId) throw new Error('User ID is required');

  try {
    await createTodoList(userId, {
      title: 'Personal Tasks',
      description: 'My personal to-dos',
      context: LIST_CONTEXT.PERSONAL,
      icon: '✅',
      color: '#06D6A0',
      isDefault: true,
      sortOrder: 0
    });

    await createTodoList(userId, {
      title: 'Financial Tasks',
      description: 'Bills, budgets, and money matters',
      context: LIST_CONTEXT.PERSONAL,
      icon: '💰',
      color: '#FFD60A',
      isDefault: false,
      sortOrder: 1
    });

    return true;
  } catch (error) {
    console.error('Error creating default lists:', error);
    throw error;
  }
}

/**
 * Get task statistics
 */
export async function getTaskStats(userId) {
  if (!userId) throw new Error('User ID is required');

  try {
    const meta = await getTodoMeta(userId);
    const todayTasks = await getFilteredTasks(userId, 'today');
    const overdueTasks = await getFilteredTasks(userId, 'overdue');
    const upcomingTasks = await getFilteredTasks(userId, 'upcoming');
    const allTasks = await getAllTasks(userId);

    // Calculate total financial amount from incomplete financial tasks
    const totalFinancialAmount = allTasks
      .filter(task => task.isFinancialTask && task.status !== TASK_STATUS.DONE && task.estimatedAmount)
      .reduce((sum, task) => sum + (parseFloat(task.estimatedAmount) || 0), 0);

    return {
      total: meta.totalTasks,
      completed: meta.completedTasks,
      completionRate: meta.completionRate7d,
      financial: meta.financialTasksCount,
      financialAmount: totalFinancialAmount,
      overdue: meta.overdueCount,
      streak: meta.streakDays,
      today: todayTasks.length,
      upcoming: upcomingTasks.length
    };
  } catch (error) {
    console.error('Error fetching task stats:', error);
    throw error;
  }
}

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to tasks from a specific list in real-time
 * @param {string} userId - User ID
 * @param {string} listId - List ID
 * @param {function} onUpdate - Callback with updated tasks array
 * @param {Object} options - Filter options (status, priority, etc.)
 * @returns {function} Unsubscribe function
 */
export function subscribeToTasksByList(userId, listId, onUpdate, options = {}) {
  if (!userId || !listId) {
    console.warn('[FINTASKS] subscribeToTasksByList: Missing userId or listId');
    onUpdate([]);
    return () => {};
  }

  const tasksRef = collection(db, 'users', userId, 'todoLists', listId, 'tasks');
  let q = query(tasksRef, orderBy('createdAt', 'desc'));

  // Apply status filter
  if (options.status) {
    q = query(q, where('status', '==', options.status));
  }

  // Apply limit
  if (options.limit) {
    q = query(q, limit(options.limit));
  }

  return onSnapshot(q, 
    (snapshot) => {
      const tasks = snapshot.docs.map(doc => ({
        ...normalizeTaskFromFirestore(doc),
        listId: listId
      }));
      console.log('[FINTASKS] Task subscription update for list', listId, ':', tasks.length, 'tasks');
      onUpdate(tasks);
    },
    (error) => {
      console.error('[FINTASKS] Task subscription error:', error);
      onUpdate([]);
    }
  );
}

/**
 * Subscribe to ALL tasks across all lists in real-time
 * This sets up subscriptions for each list and merges the results
 * @param {string} userId - User ID  
 * @param {function} onUpdate - Callback with updated tasks array
 * @param {Object} options - Filter options (status, excludeArchived, etc.)
 * @returns {function} Unsubscribe function
 */
export function subscribeToAllTasks(userId, onUpdate, options = {}) {
  if (!userId) {
    console.warn('[FINTASKS] subscribeToAllTasks: Missing userId');
    onUpdate([]);
    return () => {};
  }

  const unsubscribers = [];
  const listTasks = new Map(); // Map<listId, Task[]>
  let lists = [];

  // Function to merge and deliver all tasks
  const deliverTasks = () => {
    const allTasks = [];
    for (const [listId, tasks] of listTasks) {
      const list = lists.find(l => l.id === listId);
      allTasks.push(...tasks.map(task => ({
        ...task,
        listId,
        listTitle: list?.title || 'Unknown List'
      })));
    }

    // Filter archived if needed
    let filtered = allTasks;
    if (options.excludeArchived !== false) {
      filtered = allTasks.filter(t => t.status !== TASK_STATUS.ARCHIVED);
    }

    // Sort by createdAt desc
    filtered.sort((a, b) => {
      const aDate = new Date(a.createdAt || 0);
      const bDate = new Date(b.createdAt || 0);
      return bDate - aDate;
    });

    console.log('[FINTASKS] All tasks subscription update:', filtered.length, 'tasks');
    onUpdate(filtered);
  };

  // First, fetch lists then subscribe to each
  const initSubscriptions = async () => {
    try {
      lists = await getTodoLists(userId);
      console.log('[FINTASKS] Setting up task subscriptions for', lists.length, 'lists');

      for (const list of lists) {
        const unsub = subscribeToTasksByList(
          userId,
          list.id,
          (tasks) => {
            listTasks.set(list.id, tasks);
            deliverTasks();
          },
          options
        );
        unsubscribers.push(unsub);
      }

      // If no lists, deliver empty
      if (lists.length === 0) {
        onUpdate([]);
      }
    } catch (error) {
      console.error('[FINTASKS] Error setting up task subscriptions:', error);
      onUpdate([]);
    }
  };

  initSubscriptions();

  // Return cleanup function
  return () => {
    console.log('[FINTASKS] Cleaning up', unsubscribers.length, 'task subscriptions');
    unsubscribers.forEach(unsub => unsub());
  };
}
